/**
 * HFO analysis dock (S4) — client heuristic assistive review aid.
 */

import React, { useState } from "react";
import HFODetectionService from "../services/HFODetection";
import {
  EegDockPanelChip,
  EegDockPanelNote,
  EegDockPanelStatus,
  EegDockPanelToolbar,
} from "./EegDockPanelChrome";

export const HFO_INTENDED_USE =
  "HFO markers are non-diagnostic decision-support aids for navigating high-frequency content on raw EEG. They do not provide standalone surgical or diagnostic interpretation.";

export type HfoAnalysisPanelProps = {
  channelNames?: string[];
  channelSeries?: Array<{ name: string; data?: Float32Array | number[] | null }>;
  samplingRate?: number;
  onSeek?: (timeSeconds: number) => void;
};

function toArray(data: Float32Array | number[]): number[] {
  return Array.isArray(data) ? data : Array.from(data);
}

export default function HfoAnalysisPanel({
  channelNames = [],
  channelSeries = [],
  samplingRate = 256,
  onSeek,
}: HfoAnalysisPanelProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [detections, setDetections] = useState<
    Array<{ id: string; timeSec: number; type: string; channel: string; confidence: number }>
  >([]);

  const run = () => {
    const channels =
      channelSeries.length > 0
        ? channelSeries
        : channelNames.map((name) => ({ name, data: null }));
    const usable = channels.filter((ch) => ch.data && ch.data.length > samplingRate);
    if (!usable.length) {
      setError("Load a high-sample-rate waveform window before running HFO heuristics.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const svc = new HFODetectionService();
      const signals = usable.map((ch) => toArray(ch.data as Float32Array | number[]));
      const names = usable.map((ch, i) => ch.name || `Ch${i}`);
      const result = svc.detectMultiChannelHFOs(signals, names, samplingRate);
      const rows = (result.detections || []).slice(0, 40).map((d) => ({
        id: d.hfoId,
        timeSec: d.timestamp / 1000,
        type: d.type,
        channel: d.channel,
        confidence: d.confidence,
      }));
      setDetections(rows);
      setSummary(
        `${result.detections.length} HFO event(s) · ripples ${result.rippleCount} · fast ripples ${result.fastRippleCount} (assistive heuristic)`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "HFO detection failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hfo-analysis-panel" data-testid="hfo-analysis-panel">
      <EegDockPanelNote role="note" data-testid="hfo-intended-use">
        {HFO_INTENDED_USE}
      </EegDockPanelNote>
      <EegDockPanelToolbar>
        <button
          type="button"
          className="eeg-dock-panel-chrome__btn"
          data-testid="hfo-run-local"
          disabled={busy}
          onClick={run}
        >
          {busy ? "Detecting…" : "Run HFO heuristic"}
        </button>
      </EegDockPanelToolbar>
      {error ? <EegDockPanelStatus>{error}</EegDockPanelStatus> : null}
      {summary ? (
        <EegDockPanelStatus>
          <EegDockPanelChip>{summary}</EegDockPanelChip>
        </EegDockPanelStatus>
      ) : null}
      <ul className="hfo-analysis-panel__list">
        {detections.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              className="eeg-dock-panel-chrome__btn"
              onClick={() => onSeek?.(d.timeSec)}
            >
              {d.type} · {d.channel} · {d.timeSec.toFixed(2)}s ·{" "}
              {(d.confidence * 100).toFixed(0)}%
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
